"use client";

import React from "react";
import { type Editor } from "@tiptap/react";
import {
  Bold,
  FileText,
  Italic,
  Link2,
  Strikethrough,
  Underline,
  Unlink,
} from "lucide-react";

type ContentLinkSelection = {
  contentSlug?: string;
  contentId?: string;
};

type RichTextToolbarProps = {
  editor: Editor | null;
  onRequestLink: (prevHref?: string) => void;
  onRequestContentLink: (prev?: ContentLinkSelection) => void;
};

type ToolbarButtonProps = {
  active?: boolean;
  title: string;
  onPress: () => void;
  children: React.ReactNode;
};

function ToolbarButton({
  active,
  title,
  onPress,
  children,
}: ToolbarButtonProps) {
  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onPress();
    },
    [onPress],
  );

  return (
    <button
      type="button"
      title={title}
      onPointerDown={handlePointerDown}
      className={[
        "flex h-8 w-8 items-center justify-center rounded-sm transition",
        active ? "bg-pinkCustom text-white" : "bg-white hover:bg-neutral-100",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export function RichTextToolbar({
  editor,
  onRequestLink,
  onRequestContentLink,
}: RichTextToolbarProps) {
  const [, forceRender] = React.useReducer((count) => count + 1, 0);

  React.useEffect(() => {
    if (!editor) return;

    const rerender = () => forceRender();
    editor.on("selectionUpdate", rerender);
    editor.on("transaction", rerender);

    return () => {
      editor.off("selectionUpdate", rerender);
      editor.off("transaction", rerender);
    };
  }, [editor]);

  const isActive = React.useCallback(
    (name: string, attrs?: Record<string, any>) =>
      editor?.isActive(name, attrs) ?? false,
    [editor],
  );

  const runCommand = React.useCallback(
    (command: (chain: ReturnType<Editor["chain"]>) => ReturnType<Editor["chain"]>) =>
      () => {
        if (!editor) return;
        command(editor.chain().focus()).run();
      },
    [editor],
  );

  const handleLinkToggle = React.useCallback(() => {
    if (!editor) return;

    if (editor.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    const prevHref = editor.getAttributes("link")?.href as string | undefined;
    onRequestLink(prevHref);
  }, [editor, onRequestLink]);

  const handleContentLinkToggle = React.useCallback(() => {
    if (!editor) return;

    if (editor.isActive("contentLink")) {
      editor
        .chain()
        .focus()
        .extendMarkRange("contentLink")
        .unsetMark("contentLink")
        .run();
      return;
    }

    const attrs = editor.getAttributes("contentLink") ?? {};
    onRequestContentLink({
      contentSlug:
        typeof attrs.contentSlug === "string" ? attrs.contentSlug : undefined,
      contentId: typeof attrs.contentId === "string" ? attrs.contentId : undefined,
    });
  }, [editor, onRequestContentLink]);

  return (
    <div
      className="flex flex-row items-center gap-1 sm:flex-col"
      onMouseDown={(event) => event.preventDefault()}
    >
      <ToolbarButton
        title="Bold"
        active={isActive("bold")}
        onPress={runCommand((chain) => chain.toggleBold())}
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={isActive("italic")}
        onPress={runCommand((chain) => chain.toggleItalic())}
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={isActive("underline")}
        onPress={runCommand((chain) => chain.toggleUnderline())}
      >
        <Underline className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        active={isActive("strike")}
        onPress={runCommand((chain) => chain.toggleStrike())}
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </ToolbarButton>

      <div className="my-1 h-px w-5 bg-neutral-200 sm:h-px sm:w-5" />

      <ToolbarButton
        title={isActive("link") ? "Remove link" : "Add link"}
        active={isActive("link")}
        onPress={handleLinkToggle}
      >
        {isActive("link") ? (
          <Unlink className="h-3.5 w-3.5" />
        ) : (
          <Link2 className="h-3.5 w-3.5" />
        )}
      </ToolbarButton>
      <ToolbarButton
        title={
          isActive("contentLink") ? "Remove content link" : "Add content link"
        }
        active={isActive("contentLink")}
        onPress={handleContentLinkToggle}
      >
        <FileText className="h-3.5 w-3.5" />
      </ToolbarButton>
    </div>
  );
}
